import { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import { toast } from "sonner";
import { FileText, Download, Loader2, Search, RefreshCw, AlertCircle, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function formatPrice(v) {
  if (v == null) return "€0,00";
  return new Intl.NumberFormat("nl-NL", { style: "currency", currency: "EUR" }).format(v);
}
function formatDate(iso) {
  if (!iso) return "-";
  const d = new Date(iso);
  return d.toLocaleDateString("nl-NL", { day: "2-digit", month: "short", year: "numeric" });
}

const MONTHS_NL = ["", "Januari", "Februari", "Maart", "April", "Mei", "Juni",
                   "Juli", "Augustus", "September", "Oktober", "November", "December"];

export function InvoicesTab({ token }) {
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);
  const [year, setYear] = useState(new Date().getFullYear());
  const [month, setMonth] = useState(0); // 0 = alle
  const [q, setQ] = useState("");
  const [invoices, setInvoices] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [retrying, setRetrying] = useState(null);

  const fetchInvoices = useCallback(async () => {
    setLoading(true);
    try {
      const params = { year, page: 1, limit: 100 };
      if (month) params.month = month;
      if (q) params.q = q;
      const { data } = await axios.get(`${API}/admin/invoices`, { params, headers });
      setInvoices(data.invoices || []);
      setTotal(data.total || 0);
    } catch {
      toast.error("Facturen laden mislukt");
    }
    setLoading(false);
  }, [year, month, q, headers]);

  useEffect(() => { fetchInvoices(); }, [fetchInvoices]);

  const downloadPdf = async (order_id, invoice_number) => {
    try {
      const resp = await axios.get(`${API}/admin/invoices/${order_id}/pdf`, {
        headers, responseType: "blob",
      });
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Factuur-${invoice_number}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("PDF downloaden mislukt");
    }
  };

  const retryEboek = async (order_id) => {
    setRetrying(order_id);
    try {
      const { data } = await axios.post(`${API}/admin/orders/${order_id}/eboekhouden-retry`, {}, { headers });
      if (data.success) toast.success(`Factuur ${data.invoice_number} verstuurd naar e-Boekhouden`);
      else toast.error(`e-Boekhouden mislukt: ${(data.error || "").slice(0, 120)}`);
      fetchInvoices();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Retry mislukt");
    }
    setRetrying(null);
  };

  const YEARS = useMemo(() => {
    const now = new Date().getFullYear();
    return [now - 1, now, now + 1];
  }, []);

  return (
    <div data-testid="admin-invoices-tab">
      <div className="flex flex-wrap items-center gap-3 mb-5">
        <div className="flex items-center gap-2 bg-[#111] border border-[#1E1E1E] rounded-lg px-2 py-1">
          <span className="text-[#666] text-xs uppercase tracking-wider px-2">Jaar</span>
          {YEARS.map((y) => (
            <button key={y} onClick={() => setYear(y)} data-testid={`year-${y}`}
              className={`px-3 py-1.5 rounded-md text-sm font-semibold transition-all ${year === y ? "bg-[#FF3B30] text-white" : "text-[#888] hover:text-white"}`}>{y}</button>
          ))}
        </div>
        <div className="flex items-center gap-2 bg-[#111] border border-[#1E1E1E] rounded-lg px-2 py-1 overflow-x-auto">
          <span className="text-[#666] text-xs uppercase tracking-wider px-2">Maand</span>
          <button onClick={() => setMonth(0)} data-testid="month-all"
            className={`px-3 py-1.5 rounded-md text-sm font-semibold whitespace-nowrap ${!month ? "bg-[#FF3B30] text-white" : "text-[#888] hover:text-white"}`}>Alle</button>
          {MONTHS_NL.slice(1).map((m, i) => (
            <button key={m} onClick={() => setMonth(i + 1)} data-testid={`month-${i + 1}`}
              className={`px-2.5 py-1.5 rounded-md text-xs font-semibold whitespace-nowrap ${month === i + 1 ? "bg-[#FF3B30] text-white" : "text-[#888] hover:text-white"}`}>{m.slice(0, 3)}</button>
          ))}
        </div>
        <div className="flex items-center gap-2 bg-[#111] border border-[#1E1E1E] rounded-lg px-3 min-w-[220px]">
          <Search size={14} className="text-[#666]" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Zoek op naam, email, factuurnr..." data-testid="invoice-search"
            className="bg-transparent border-0 text-white text-sm h-9 focus:ring-0 focus-visible:ring-0" />
        </div>
        <Button variant="ghost" size="sm" onClick={fetchInvoices} className="text-[#888] hover:text-white" data-testid="refresh-invoices">
          <RefreshCw size={16} />
        </Button>
        <div className="ml-auto text-[#666] text-xs">
          Totaal: <span className="text-white font-semibold">{total}</span> factuur/facturen
        </div>
      </div>

      <div className="bg-[#111] border border-[#1E1E1E] rounded-xl overflow-hidden" data-testid="invoices-table">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-[#1E1E1E]">
                <th className="text-left px-4 py-3.5 text-[#666] text-xs font-semibold uppercase tracking-wider">Factuurnr.</th>
                <th className="text-left px-4 py-3.5 text-[#666] text-xs font-semibold uppercase tracking-wider">Datum</th>
                <th className="text-left px-4 py-3.5 text-[#666] text-xs font-semibold uppercase tracking-wider">Klant</th>
                <th className="text-right px-4 py-3.5 text-[#666] text-xs font-semibold uppercase tracking-wider">Excl. btw</th>
                <th className="text-right px-4 py-3.5 text-[#666] text-xs font-semibold uppercase tracking-wider">BTW 21%</th>
                <th className="text-right px-4 py-3.5 text-[#666] text-xs font-semibold uppercase tracking-wider">Incl. btw</th>
                <th className="text-center px-4 py-3.5 text-[#666] text-xs font-semibold uppercase tracking-wider">e-Boekhouden</th>
                <th className="text-right px-4 py-3.5 text-[#666] text-xs font-semibold uppercase tracking-wider">PDF</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={8} className="text-center py-12"><Loader2 size={22} className="animate-spin text-[#FF3B30] mx-auto" /></td></tr>
              ) : invoices.length === 0 ? (
                <tr><td colSpan={8} className="text-center py-12 text-[#555] text-sm">Geen facturen gevonden voor deze periode</td></tr>
              ) : invoices.map((inv) => (
                <tr key={inv.id} className="border-b border-[#1E1E1E]/50 hover:bg-[#1E1E1E]/30 transition-colors" data-testid={`invoice-row-${inv.invoice_number}`}>
                  <td className="px-4 py-3.5 text-[#FF3B30] font-mono font-bold text-sm">{inv.invoice_number}</td>
                  <td className="px-4 py-3.5 text-[#888] text-sm">{formatDate(inv.invoice_issued_at || inv.paid_at)}</td>
                  <td className="px-4 py-3.5">
                    <p className="text-white text-sm">{inv.name}</p>
                    <p className="text-[#555] text-xs">{inv.email}</p>
                  </td>
                  <td className="px-4 py-3.5 text-right text-[#ccc] text-sm">{formatPrice(inv.total_ex_vat)}</td>
                  <td className="px-4 py-3.5 text-right text-[#F1C40F] text-sm">{formatPrice(inv.vat_amount)}</td>
                  <td className="px-4 py-3.5 text-right text-white text-sm font-bold">{formatPrice(inv.total_incl_vat || inv.total)}</td>
                  <td className="px-4 py-3.5 text-center">
                    {inv.eboek_invoice_id ? (
                      <span className="inline-flex items-center gap-1 text-[#6AB04C] text-xs" data-testid={`eboek-ok-${inv.invoice_number}`} title={`e-Boekhouden ID: ${inv.eboek_invoice_id}`}>
                        <CheckCircle2 size={13} /> Geboekt
                      </span>
                    ) : inv.eboek_error ? (
                      <div className="inline-flex items-center gap-2">
                        <span className="inline-flex items-center gap-1 text-[#E74C3C] text-xs" title={inv.eboek_error}><AlertCircle size={13} /> Fout</span>
                        <Button variant="ghost" size="sm" className="h-6 px-2 text-[#F39C12] text-xs" onClick={() => retryEboek(inv.id)} disabled={retrying === inv.id} data-testid={`retry-eboek-${inv.invoice_number}`}>
                          {retrying === inv.id ? <Loader2 size={12} className="animate-spin" /> : "Opnieuw"}
                        </Button>
                      </div>
                    ) : (
                      <div className="inline-flex items-center gap-2">
                        <span className="text-[#666] text-xs">–</span>
                        <Button variant="ghost" size="sm" className="h-6 px-2 text-[#F39C12] text-xs" onClick={() => retryEboek(inv.id)} disabled={retrying === inv.id} data-testid={`send-eboek-${inv.invoice_number}`}>
                          {retrying === inv.id ? <Loader2 size={12} className="animate-spin" /> : "Verzend"}
                        </Button>
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3.5 text-right">
                    <Button variant="ghost" size="sm" className="text-[#888] hover:text-[#FF3B30]" onClick={() => downloadPdf(inv.id, inv.invoice_number)} data-testid={`download-pdf-${inv.invoice_number}`}>
                      <Download size={15} />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

export function VatReportTab({ token }) {
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);
  const [year, setYear] = useState(new Date().getFullYear());
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancel = false;
    setLoading(true);
    axios.get(`${API}/admin/vat-report`, { params: { year }, headers })
      .then((r) => { if (!cancel) setReport(r.data); })
      .catch(() => toast.error("BTW-rapport laden mislukt"))
      .finally(() => { if (!cancel) setLoading(false); });
    return () => { cancel = true; };
  }, [year, headers]);

  const YEARS = useMemo(() => {
    const now = new Date().getFullYear();
    return [now - 1, now, now + 1];
  }, []);

  return (
    <div data-testid="admin-vat-tab">
      <div className="flex items-center gap-4 mb-6">
        <div className="flex items-center gap-2 bg-[#111] border border-[#1E1E1E] rounded-lg px-2 py-1">
          <span className="text-[#666] text-xs uppercase tracking-wider px-2">Jaar</span>
          {YEARS.map((y) => (
            <button key={y} onClick={() => setYear(y)} data-testid={`vat-year-${y}`}
              className={`px-3 py-1.5 rounded-md text-sm font-semibold transition-all ${year === y ? "bg-[#FF3B30] text-white" : "text-[#888] hover:text-white"}`}>{y}</button>
          ))}
        </div>
        <p className="text-[#555] text-xs">BTW-tarief: <span className="text-white font-semibold">{report?.vat_rate || 21}%</span></p>
      </div>

      {loading || !report ? (
        <div className="flex justify-center py-20"><Loader2 size={28} className="animate-spin text-[#FF3B30]" /></div>
      ) : (
        <>
          {/* Jaartotaal */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6" data-testid="vat-year-totals">
            {[
              { label: "Omzet (excl. btw)", value: formatPrice(report.total.omzet_ex_vat), color: "#3498DB" },
              { label: "BTW (21%)", value: formatPrice(report.total.btw_bedrag), color: "#F1C40F" },
              { label: "Totaal (incl. btw)", value: formatPrice(report.total.totaal_incl_vat), color: "#FF3B30" },
              { label: "Aantal facturen", value: report.total.aantal_facturen, color: "#6AB04C" },
            ].map((s) => (
              <div key={s.label} className="bg-[#111] border border-[#1E1E1E] rounded-xl p-4">
                <p className="text-[#666] text-xs uppercase tracking-wider mb-2">{s.label}</p>
                <p className="text-xl font-bold" style={{ color: s.color }}>{s.value}</p>
              </div>
            ))}
          </div>

          {/* Kwartaal — voor BTW aangifte */}
          <h3 className="text-[#888] text-xs uppercase tracking-widest mb-2">Per kwartaal (BTW aangifte)</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8" data-testid="vat-quarters">
            {report.quarters.map((q) => (
              <div key={q.quarter} className="bg-[#111] border border-[#1E1E1E] rounded-xl p-4" data-testid={`quarter-${q.quarter}`}>
                <p className="text-[#666] text-xs uppercase tracking-wider mb-2">Q{q.quarter} {year}</p>
                <div className="space-y-1 text-xs">
                  <div className="flex justify-between"><span className="text-[#888]">Omzet excl.</span><span className="text-[#ccc]">{formatPrice(q.omzet_ex_vat)}</span></div>
                  <div className="flex justify-between"><span className="text-[#888]">BTW 21%</span><span className="text-[#F1C40F] font-semibold">{formatPrice(q.btw_bedrag)}</span></div>
                  <div className="flex justify-between border-t border-[#1E1E1E] mt-1 pt-1"><span className="text-[#888]">Incl.</span><span className="text-white font-bold">{formatPrice(q.totaal_incl_vat)}</span></div>
                  <div className="flex justify-between"><span className="text-[#666]">Facturen</span><span className="text-[#666]">{q.aantal_facturen}</span></div>
                </div>
              </div>
            ))}
          </div>

          {/* Per maand */}
          <h3 className="text-[#888] text-xs uppercase tracking-widest mb-2">Per maand</h3>
          <div className="bg-[#111] border border-[#1E1E1E] rounded-xl overflow-hidden" data-testid="vat-months">
            <table className="w-full">
              <thead>
                <tr className="border-b border-[#1E1E1E]">
                  <th className="text-left px-4 py-3 text-[#666] text-xs font-semibold uppercase tracking-wider">Maand</th>
                  <th className="text-right px-4 py-3 text-[#666] text-xs font-semibold uppercase tracking-wider">Omzet excl.</th>
                  <th className="text-right px-4 py-3 text-[#666] text-xs font-semibold uppercase tracking-wider">BTW 21%</th>
                  <th className="text-right px-4 py-3 text-[#666] text-xs font-semibold uppercase tracking-wider">Totaal incl.</th>
                  <th className="text-right px-4 py-3 text-[#666] text-xs font-semibold uppercase tracking-wider">Facturen</th>
                </tr>
              </thead>
              <tbody>
                {report.months.map((m) => (
                  <tr key={m.month} className="border-b border-[#1E1E1E]/50 hover:bg-[#1E1E1E]/30 transition-colors" data-testid={`vat-month-${m.month}`}>
                    <td className="px-4 py-3 text-white text-sm">{MONTHS_NL[m.month]}</td>
                    <td className="px-4 py-3 text-right text-[#ccc] text-sm">{formatPrice(m.omzet_ex_vat)}</td>
                    <td className="px-4 py-3 text-right text-[#F1C40F] text-sm">{formatPrice(m.btw_bedrag)}</td>
                    <td className="px-4 py-3 text-right text-white font-semibold text-sm">{formatPrice(m.totaal_incl_vat)}</td>
                    <td className="px-4 py-3 text-right text-[#666] text-sm">{m.aantal_facturen}</td>
                  </tr>
                ))}
                <tr className="bg-[#1a1a1a]">
                  <td className="px-4 py-3 text-white font-bold text-sm">Jaartotaal</td>
                  <td className="px-4 py-3 text-right text-white font-bold text-sm">{formatPrice(report.total.omzet_ex_vat)}</td>
                  <td className="px-4 py-3 text-right text-[#F1C40F] font-bold text-sm">{formatPrice(report.total.btw_bedrag)}</td>
                  <td className="px-4 py-3 text-right text-[#FF3B30] font-bold text-sm">{formatPrice(report.total.totaal_incl_vat)}</td>
                  <td className="px-4 py-3 text-right text-white font-bold text-sm">{report.total.aantal_facturen}</td>
                </tr>
              </tbody>
            </table>
          </div>

          <p className="mt-4 text-[#555] text-[11px]">
            <FileText size={11} className="inline -mt-0.5" /> Alleen bestellingen met status <b>betaald</b> (dus met factuurnummer) worden meegeteld.
            Gebruik deze cijfers als leidraad voor je BTW-aangifte — controleer altijd tegen je e-Boekhouden administratie.
          </p>
        </>
      )}
    </div>
  );
}
